/**
 * Client for the vlj-storefront Worker (see dashboard/src/storefront.js),
 * shared by the order tracker and withdrawal pages.
 *
 * Bodies go as text/plain so the browser skips the CORS preflight.
 */
(() => {
  if (window.VljStorefront) return;

  const PREFILL_KEY = 'vlj_withdrawal_prefill';

  async function post(path, body) {
    const endpoint = window.vljStorefront?.endpoint;
    if (!endpoint) return { ok: false, status: 0, data: { error: 'unavailable' } };
    try {
      const response = await fetch(endpoint + path, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, data };
    } catch {
      return { ok: false, status: 0, data: { error: 'unavailable' } };
    }
  }

  function formatDate(ms, withTime = false) {
    if (!ms) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(new Date(ms));
  }

  /** Safe for both text and attribute values. */
  function escape(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }

  /**
   * Fills a translated string. Sections render placeholders as [key] (the
   * same convention as the wishlist page's data-removed-label).
   */
  function format(template, values = {}) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  /**
   * Reads the translated strings a section renders into <script type="application/json" data-strings>.
   * Liquid's `t` filter HTML-escapes its output (it's meant for markup), so
   * entities are decoded here; callers escape again when building HTML.
   */
  function strings(root) {
    try {
      const raw = JSON.parse(root.querySelector('[data-strings]')?.textContent || '{}');
      const decoder = document.createElement('textarea');
      return Object.fromEntries(
        Object.entries(raw).map(([key, value]) => {
          decoder.innerHTML = value;
          return [key, decoder.value];
        })
      );
    } catch {
      return {};
    }
  }

  /**
   * The tracker hands an order to the withdrawal page through sessionStorage,
   * so the customer's email never ends up in a URL, browser history or logs.
   */
  const prefill = {
    set(value) {
      try {
        sessionStorage.setItem(PREFILL_KEY, JSON.stringify(value));
      } catch {}
    },
    take() {
      try {
        const value = JSON.parse(sessionStorage.getItem(PREFILL_KEY) || 'null');
        sessionStorage.removeItem(PREFILL_KEY);
        return value;
      } catch {
        return null;
      }
    },
  };

  window.VljStorefront = { post, formatDate, escape, format, strings, prefill };
})();
